/**
 * In-IDE upgrade modal — the Pricing/Billing page content rendered in a modal
 * so upgrade CTAs inside the workspace never navigate the IDE tab or spawn a
 * new one. `AuthModalMount`'s `onUpgradeIntercept` routes intercepted
 * `/pricing` and `/billing` clicks here (see Workspace).
 *
 * Stripe checkout is the one step that still opens a new tab — an external
 * payment page can't render in-app and the IDE tab must never navigate. The
 * same focus-return pattern as AuthModalMount's default flow refreshes the
 * session + usage in place when the user comes back from checkout.
 *
 * @module
 */

import { useEffect, useRef } from 'react'

import { useAuth } from '@molecule/app-react'
import { Modal } from '@molecule/app-ui-react'

import { BillingContent } from '../pages/Billing.js'
import { PricingContent } from '../pages/Pricing.js'
import { notifyUsageInvalidated } from '../utilities/usage-utilities.js'

/** Which page body the modal shows. */
export type UpgradeModalView = 'pricing' | 'billing'

/** Props for {@link UpgradeModal}. */
export interface UpgradeModalProps {
  /** The view to show, or `null` when closed. */
  view: UpgradeModalView | null
  onClose: () => void
  /** Switch views in place (Billing's upgrade CTAs show the pricing view). */
  onViewChange: (view: UpgradeModalView) => void
}

/**
 * Renders the in-IDE pricing/billing modal.
 *
 * @param props - {@link UpgradeModalProps}.
 * @returns The modal (renders nothing until a view is set).
 */
export function UpgradeModal({ view, onClose, onViewChange }: UpgradeModalProps) {
  const { refresh } = useAuth()

  // Set when a Stripe checkout tab was opened from the pricing view, so a
  // completed upgrade reflects in place when the user returns to this tab.
  const checkoutOpenedRef = useRef(false)
  useEffect(() => {
    const onFocus = (): void => {
      if (!checkoutOpenedRef.current) return
      checkoutOpenedRef.current = false
      void refresh()
      notifyUsageInvalidated()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refresh])

  return (
    <Modal
      open={view != null}
      onClose={onClose}
      size="xl"
      // Three tier cards side by side need more than the xl preset (896px);
      // 1200 lets each card reach its 360px max so the rate-card lines don't
      // wrap. Specific widths are the sanctioned inline-style exception.
      style={{ maxWidth: 'min(1200px, calc(100vw - 32px))' }}
      data-mol-id="upgrade-modal"
    >
      <div style={{ padding: '0 8px 8px' }}>
        {view === 'billing' ? (
          <BillingContent onShowPricing={() => onViewChange('pricing')} />
        ) : (
          <PricingContent
            variant="modal"
            onCheckoutOpened={() => {
              checkoutOpenedRef.current = true
            }}
            // A subscriber's Free card cancels: send them to the billing view,
            // where "Manage billing" opens the provider portal.
            onShowBilling={() => onViewChange('billing')}
          />
        )}
      </div>
    </Modal>
  )
}

UpgradeModal.displayName = 'UpgradeModal'
